import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Eye, Trash2, Edit, Landmark, RefreshCw } from 'lucide-react';

const BankPMManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      const response = await axios.get(`${API}/bank-pm-inspections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to fetch inspections');
    }
  };

  const handleDelete = (inspection) => {
    setInspectionToDelete(inspection);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!inspectionToDelete) return;
    
    try {
      await axios.delete(`${API}/bank-pm-inspections/${inspectionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inspection deleted successfully');
      setShowDeleteDialog(false);
      setInspectionToDelete(null);
      fetchInspections();
    } catch (error) {
      toast.error('Failed to delete inspection');
      setShowDeleteDialog(false);
      setInspectionToDelete(null);
    }
  };

  const handleView = (inspection) => {
    setSelectedInspection(inspection);
    setShowViewDialog(true);
  };

  const handleEdit = (inspection) => {
    navigate('/field/bank-pm', { state: { editData: inspection } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      
      <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Landmark className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-bold">Bank PM Management</h2>
        </div>
        <Button onClick={fetchInspections} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Bank PM Inspections</CardTitle>
        </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {inspections.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No PM inspections found</p>
          ) : (
            inspections.map((pm) => (
              <div key={pm.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-lg">{pm.inspection_number}</p>
                    <p className="text-sm text-slate-600">Date: {_fmtDate(pm.date_of_inspection)}</p>
                    {pm.building_name && <p className="text-sm text-slate-600">Building: {pm.building_name}</p>}
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => handleView(pm)}>
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(pm)}>
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(pm)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>

    {/* View Dialog */}
    <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bank PM Inspection Details</DialogTitle>
        </DialogHeader>
        {selectedInspection && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-600">Inspection Number</p>
              <p className="text-base">{selectedInspection.inspection_number}</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-600">Date of Inspection</p>
              <p className="text-base">{_fmtDate(selectedInspection.date_of_inspection)}</p>
            </div>
            {selectedInspection.building_name && (
              <div>
                <p className="text-sm font-semibold text-gray-600">Building Name</p>
                <p className="text-base">{selectedInspection.building_name}</p>
              </div>
            )}
            {selectedInspection.inspector_name && (
              <div>
                <p className="text-sm font-semibold text-gray-600">Inspector</p>
                <p className="text-base">{selectedInspection.inspector_name}</p>
              </div>
            )}
            <div className="text-sm text-gray-500 pt-4 border-t">
              Submitted: {_fmtDateTime(selectedInspection.submitted_at || selectedInspection.date_of_inspection)}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowViewDialog(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Delete</DialogTitle>
        </DialogHeader>
        <p>Are you sure you want to delete inspection "{inspectionToDelete?.inspection_number}"? This action cannot be undone.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={confirmDelete}>
            Delete Inspection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </div>
    </div>
  );
};

export default BankPMManagement;
